import { useCallback, useContext } from "react"
import { xml } from "@xmpp/client"
import { v4 as uuid } from "uuid"
import { toast } from "sonner"
import { format } from "date-fns"
import i18n from "@/lib/i18n/i18n"
import { XmppContext } from "../stream/xmppClient"
import { useAppSelector, useAppDispatch, useAppStore } from "../reducers/hooks"
import {
  selectActiveChat,
  selectDriverParticipant,
  selectMessageChat,
} from "../reducers/chatsSliceSelectors"
import { Message } from "../reducers/messagesSlice"
import { useDocumentLink } from "./useDocumentLink"
import useCMRMetadata from "./useCMRMetadata"
import { sendGroupchatStanza } from "../reducers/chatsSliceThunks"
import { isManualDocumentLinkingEnabled } from "@/lib/gpsDocumentLinks"

const useFindDeliveryOrder = (message: Message) => {
  if (!message) return undefined
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { myJid, client } = useContext(XmppContext)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const dispatch = useAppDispatch()
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const store = useAppStore()
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const activeChat = useAppSelector(selectActiveChat)

  const isForwarded = !!message.forwarded
  const cmrMessage = isForwarded ? message.forwarded : message
  const url = cmrMessage?.url

  const isCmrRoom = activeChat.jid.startsWith("cmr@automation")
  const processedMessage = isForwarded
    ? (message.forwarded ?? ({} as Message))
    : message
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const messageChat = useAppSelector(
    selectMessageChat(processedMessage, myJid.toString()),
  )

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { linkType } = useDocumentLink(message)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { documentMetadata, driverContext } = useCMRMetadata(message)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const driver = useAppSelector(selectDriverParticipant(activeChat.jid))

  const isCmrFile = url && /\.png|\.jpg|\.jpeg|\.pdf/i.test(url)
  const alreadyLinked = linkType === "deliveryOrder"
  const shouldShow =
    isManualDocumentLinkingEnabled(myJid?.getDomain()) &&
    isCmrFile &&
    !alreadyLinked

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const callback = useCallback(async () => {
    const userLocal = myJid.getLocal()
    const domain = myJid.getDomain()
    const transchatJid = `${userLocal}_transchat@conference.${domain}`

    const lines: string[] = []

    // Driver info
    if (driver) {
      const driverName =
        driver.name ||
        [driver.firstName, driver.lastName].filter(Boolean).join(" ")
      if (driverName) lines.push(`Kierowca: ${driverName}`)
      if (driver.plates) lines.push(`Tablice: ${driver.plates}`)
      if (driver.phone) lines.push(`Telefon: ${driver.phone}`)
    }

    // Timestamp
    if (message.timestamp) {
      lines.push(
        `Data wysłania: ${format(new Date(message.timestamp), "yyyy-MM-dd HH:mm")}`,
      )
    }

    // All extracted metadata
    const data = documentMetadata?.data
    if (data) {
      for (const [key, value] of Object.entries(data)) {
        if (value) lines.push(`${key}: ${value}`)
      }
    }

    // Driver context (vehicle, location)
    if (driverContext) {
      if (driverContext.plates)
        lines.push(`Pojazd (kontekst): ${driverContext.plates}`)
      if (driverContext.location?.formattedAddress)
        lines.push(`Lokalizacja: ${driverContext.location.formattedAddress}`)
      if (driverContext.location?.odometer)
        lines.push(`Przebieg: ${driverContext.location.odometer}`)
    }

    // Source room
    const sourceRoomJid = isCmrRoom ? messageChat?.jid : activeChat.jid
    if (sourceRoomJid) lines.push(`Pokój: ${sourceRoomJid}`)

    // Message ID
    if (cmrMessage?.id) lines.push(`ID wiadomości: ${cmrMessage.id}`)

    // File info
    if (cmrMessage?.fileName) lines.push(`Nazwa pliku: ${cmrMessage.fileName}`)
    if (url) lines.push(`URL: ${url}`)

    const metaBlock = lines.length > 0 ? "\n" + lines.join("\n") : ""
    const body = `Znajdź zlecenie dla tego CMR i zalinkuj.${metaBlock}`

    const stanza = xml(
      "message",
      {
        type: "groupchat",
        to: transchatJid,
        from: myJid.toString(),
        id: uuid(),
      },
      xml("body", {}, body),
      xml("cmr-link-request", {
        xmlns: "urn:slickshift:cmr-link-request:0",
        file_url: url || "",
        file_name: cmrMessage?.fileName || "",
      }),
      xml("markable", "urn:xmpp:chat-markers:0"),
    )

    sendGroupchatStanza(client, dispatch, stanza, store.getState)
    toast(i18n.t("ai.findDeliveryOrder.searching"), {
      classNames: { toast: "!justify-center" },
    })
  }, [
    myJid,
    store,
    client,
    dispatch,
    message.timestamp,
    documentMetadata,
    driverContext,
    driver,
    url,
    cmrMessage,
    isCmrRoom,
    messageChat,
    activeChat,
  ])

  return shouldShow ? callback : undefined
}

export default useFindDeliveryOrder
